import { Row, Col, Card } from "react-bootstrap";
import { BandMember } from "./content";
import { FieldSpec, useImageSrc, useSite } from "./content/context";
import { AddTile, EditableText, ItemControls } from "./edit/primitives";
import { compact, moveItem, removeItem, replaceItem } from "./edit/list";

const memberFields: FieldSpec[] = [
  { name: "name", label: "Name", required: true },
  { name: "role", label: "Role", placeholder: "Guitar" },
  { name: "image", label: "Photo", type: "image" },
  { name: "bio", label: "Bio", type: "textarea", hint: "Shown when visitors hover over the photo." },
];

const BandMembers = () => {
  const { data, edit } = useSite();
  const imageSrc = useImageSrc();
  const members = data.members;
  const save = (next: BandMember[]) => edit?.update("members", next);
  const setField = (index: number, field: keyof BandMember, value: string) =>
    save(replaceItem(members, index, compact<BandMember>({ ...(members[index] as unknown as Record<string, string>), [field]: value })));

  const openMember = async (index?: number) => {
    if (!edit) return;
    const current = index === undefined ? undefined : members[index];
    const result = await edit.openForm({
      title: current ? `Edit ${current.name}` : "Add a band member",
      fields: memberFields,
      values: { ...current },
      submitLabel: current ? "Save" : "Add member",
    });
    if (!result || !("values" in result)) return;
    const member = compact<BandMember>(result.values);
    save(index === undefined ? [...members, member] : replaceItem(members, index, member));
  };

  return (
    <Row className="mb-3">
      {members.map((member, index) => (
        <Col key={`${member.name}-${index}`} xs={12} sm={6} md={3} className="mb-3">
          <Card className={`member-card${edit ? " af-frame" : " fancy-hover"}`}>
            <div className="card-image-wrapper">
              <Card.Img variant="top" src={imageSrc(member.image)} alt={member.name} className="card-image" />
              <Card.Body>
                <Card.Title>
                  <EditableText label="Band member name" value={member.name} onChange={(v) => setField(index, "name", v)} validate={(v) => (v ? undefined : "A band member needs a name.")} />
                </Card.Title>
                <Card.Text>
                  <EditableText label="Role" value={member.role} onChange={(v) => setField(index, "role", v)} />
                </Card.Text>
              </Card.Body>
              {/* The hover bio would cover the edit buttons; it's edited in the details popup instead. */}
              {edit ? null : (
                <div className="overlay">
                  <div className="overlay-content">
                    {member.bio && <small>{member.bio}</small>}
                  </div>
                </div>
              )}
            </div>
            <ItemControls
              noun="band member"
              index={index}
              count={members.length}
              onChangePhoto={(path) => setField(index, "image", path)}
              onEdit={() => openMember(index)}
              onMove={(from, to) => save(moveItem(members, from, to))}
              onRemove={() => save(removeItem(members, index))}
            />
          </Card>
        </Col>
      ))}
      {edit ? (
        <Col xs={12} sm={6} md={3} className="mb-3">
          <AddTile label="Add a band member" onClick={() => openMember()} />
        </Col>
      ) : null}
    </Row>
  );
};

export default BandMembers
